import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { FilterOption } from '@/types/filters.types';
import { listFacilities } from '@/services/admin.service';
import { appointmentsService } from '@/services/appointments.service';
import { MEMBER_STATUS_OPTIONS } from '@/lib/filterRegistry';
import { canonicalizeCategories, majorityCategory, type CategoryGroup } from './appointmentTypeCategories';

const STATUS_FILTER_OPTIONS: FilterOption[] = [
  { label: 'Scheduled', value: 'Scheduled' },
  { label: 'Contact Attempted', value: 'Contact Attempted' },
  { label: 'Confirmed', value: 'Confirmed' },
  { label: 'Waiting List', value: 'Waiting List' },
  { label: 'No Show', value: 'No Show' },
  { label: 'Cancelled', value: 'Cancelled' },
  { label: 'Reschedule Pending', value: 'Reschedule Pending' },
  { label: 'Checked In', value: 'Checked In' },
  { label: 'Checked Back', value: 'Checked Back' },
  // Label matches the "Completed" status badge in the list; value must stay
  // 'Checked Out' — it's the canonical legacy filter value stored in saved lists.
  { label: 'Completed', value: 'Checked Out' },
  { label: 'Action Required', value: 'action_required' },
];

// Two different "first" concepts, deliberately both offered:
//   first_time  — the manual switch on the appointment (first of this TYPE),
//                 stored on appointment_addons.firstAppointment.
//   first_visit — derived server-side, the patient's very first appointment
//                 ever (API_v5 ListQueryService::firstVisitCondition).
// Mirrored server-side in MarketingController's `other` group so dynamic lists
// offer the same values.
/** Header for types whose group carries no name at all. */
const UNCATEGORIZED = 'Uncategorized';

const OTHER_FILTER_OPTIONS: FilterOption[] = [
  { value: 'first_time', label: 'First Time' },
  { value: 'first_visit', label: 'First Visit' },
  { value: 'comp', label: 'Comp' },
];

export function useAppointmentFilterOptions(opts?: { enabled?: boolean }): Record<string, FilterOption[]> {
  const enabled = opts?.enabled ?? true;

  const { data: facilities } = useQuery({
    queryKey: ['facilities'],
    queryFn: () => listFacilities(),
    staleTime: 5 * 60_000,
    enabled,
  });

  const { data: appointmentTypes } = useQuery({
    queryKey: ['appointmentTypes'],
    queryFn: () => appointmentsService.getAppointmentTypes(),
    staleTime: 5 * 60_000,
    enabled,
  });

  const { data: resources } = useQuery({
    queryKey: ['appointment-resources'],
    queryFn: () => appointmentsService.getResources(),
    staleTime: 5 * 60_000,
    enabled,
  });

  return useMemo<Record<string, FilterOption[]>>(() => {
    const providerOpts = (resources ?? [])
      .filter((r) => r.resourceType === 'doctor' && r.active)
      .map((r) => ({ value: String(r.resourceId), label: r.resourceName }));

    // One checkbox per unique type name, filed under a category header.
    //
    // Order matters and is load-bearing: AppointmentsPage builds its
    // representative-ID map from the same source in the same order and takes
    // the FIRST id per name, so this must keep de-duplicating on first-seen
    // before it sorts. Sorting the finished array is safe — the representative
    // is already chosen by then.
    const typeOpts: FilterOption[] = (() => {
      const active = (appointmentTypes ?? []).filter((t) => t.appointmentTypeGroupActive !== false);

      const groups = new Map<number, CategoryGroup>();
      for (const t of active) {
        const groupId = t.appointmentTypeGroupId;
        if (groupId == null || !t.appointmentTypeGroupName) continue;
        const existing = groups.get(groupId);
        if (existing) {
          existing.typeNames?.push(t.appointmentTypeName);
          continue;
        }
        groups.set(groupId, {
          groupId,
          groupName: t.appointmentTypeGroupName,
          groupFacilityId: t.appointmentTypeGroupFacilityId ?? 0,
          typeNames: [t.appointmentTypeName],
        });
      }
      const canonical = canonicalizeCategories([...groups.values()]);

      // A name merged across facilities can span categories; the most common
      // one wins. Collected in API order so majorityCategory's tie-break is
      // `atOrder`.
      const categoryVotes = new Map<string, string[]>();
      for (const t of active) {
        const category = t.appointmentTypeGroupId == null
          ? undefined
          : canonical.get(t.appointmentTypeGroupId);
        if (!category) continue;
        const key = t.appointmentTypeName.toLowerCase();
        const votes = categoryVotes.get(key) ?? [];
        votes.push(category);
        categoryVotes.set(key, votes);
      }

      const seen = new Set<string>();
      const collapsed = active
        .map((t) => ({
          value: String(t.appointmentTypeId),
          label: t.appointmentTypeName,
          // Every option MUST carry a group: FilterChecklist's collapsible
          // renderer iterates category names, so an option without one would
          // never render at all.
          group: majorityCategory(categoryVotes.get(t.appointmentTypeName.toLowerCase()) ?? [])
            ?? UNCATEGORIZED,
        }))
        .filter((t) => {
          const key = t.label.toLowerCase();
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });

      // Alphabetical category headers, `atOrder` preserved inside each one.
      // "Uncategorized" sinks to the bottom.
      return collapsed
        .map((option, index) => ({ option, index }))
        .sort((a, b) => {
          const aLast = a.option.group === UNCATEGORIZED ? 1 : 0;
          const bLast = b.option.group === UNCATEGORIZED ? 1 : 0;
          if (aLast !== bLast) return aLast - bLast;
          const byGroup = a.option.group.localeCompare(b.option.group);
          return byGroup !== 0 ? byGroup : a.index - b.index;
        })
        .map(({ option }) => option);
    })();

    return {
      'appointment-status': STATUS_FILTER_OPTIONS,
      facility: (facilities ?? []).filter((f) => f.active).map((f) => ({ value: String(f.facilityId), label: f.facilityName })),
      doctor: providerOpts,
      'appt-type': typeOpts,
      'member-status': MEMBER_STATUS_OPTIONS,
      other: OTHER_FILTER_OPTIONS,
    };
  }, [facilities, appointmentTypes, resources]);
}
